import { useState } from "react";
import { waLink } from "@/lib/wa";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, WhatsAppIcon } from "./Shared";
import { EmberCanvas } from "./EmberCanvas";

const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-base text-slate-100 placeholder:text-zinc-500 outline-none transition-colors duration-200 focus:border-amber-400/60 focus:bg-white/[0.07]";

const Field = ({ label, htmlFor, children }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-zinc-300">
            {label}
        </label>
        {children}
    </div>
);

export const CtaForm = () => {
    const { lang } = useLang();
    const t = STR.cta[lang];
    const [form, setForm] = useState({
        nama: "",
        properti: "",
        jenis: "Kost",
        paket: "Profesional",
        pesan: "",
    });

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const handleSubmit = (e) => {
        e.preventDefault();
        const msg = [
            "Halo TechFlaze, saya ingin konsultasi pembuatan website booking.",
            "",
            `Nama: ${form.nama}`,
            `Nama Properti: ${form.properti}`,
            `Jenis Properti: ${form.jenis}`,
            `Paket yang Diminati: ${form.paket}`,
            form.pesan ? `Pesan: ${form.pesan}` : null,
        ]
            .filter(Boolean)
            .join("\n");
        window.open(waLink(msg), "_blank", "noopener,noreferrer");
    };

    return (
        <section id="kontak" data-testid="cta-section" className="relative overflow-hidden py-24 md:py-32">
            <div
                className="absolute left-1/2 top-0 h-80 w-[44rem] -translate-x-1/2 rounded-full bg-orange-600/15 blur-[130px]"
                aria-hidden="true"
            />
            <EmberCanvas density={0.6} />
            <div className="relative mx-auto max-w-6xl px-5 md:px-8">
                <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
                    <FadeUp>
                        <span className="inline-block rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-400">
                            {t.eyebrow}
                        </span>
                        <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 md:text-4xl lg:text-5xl">
                            {t.titleA} <span className="text-gradient">{t.titleB}</span>
                        </h2>
                        <p className="mt-5 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
                            {t.desc}
                        </p>
                        <ul className="mt-8 space-y-3 text-sm text-zinc-300">
                            {t.bullets.map((b) => (
                                <li key={b} className="flex items-center gap-3">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <iframe
                                title="Peta wilayah layanan TechFlaze"
                                src="https://www.openstreetmap.org/export/embed.html?bbox=94.5%2C-11.5%2C141.5%2C6.5&layer=mapnik"
                                loading="lazy"
                                data-testid="service-map"
                                className="h-44 w-full rounded-xl border border-white/10"
                            />
                            <p className="mt-3 text-xs leading-relaxed text-zinc-500">{t.mapCaption}</p>
                        </div>
                    </FadeUp>

                    <FadeUp delay={0.15}>
                        <form onSubmit={handleSubmit} data-testid="contact-form" className="card-surface space-y-5 p-7 md:p-9">
                            <Field label={t.name} htmlFor="nama">
                                <input
                                    id="nama"
                                    type="text"
                                    required
                                    value={form.nama}
                                    onChange={set("nama")}
                                    placeholder={t.namePh}
                                    data-testid="form-input-nama"
                                    className={inputCls}
                                />
                            </Field>
                            <Field label={t.prop} htmlFor="properti">
                                <input
                                    id="properti"
                                    type="text"
                                    required
                                    value={form.properti}
                                    onChange={set("properti")}
                                    placeholder={t.propPh}
                                    data-testid="form-input-properti"
                                    className={inputCls}
                                />
                            </Field>
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <Field label={t.type} htmlFor="jenis">
                                    <select
                                        id="jenis"
                                        value={form.jenis}
                                        onChange={set("jenis")}
                                        data-testid="form-select-jenis"
                                        className={`${inputCls} appearance-none`}
                                    >
                                        <option className="bg-[#121212]" value="Kost">Kost</option>
                                        <option className="bg-[#121212]" value="Villa">Villa</option>
                                        <option className="bg-[#121212]" value="Hotel">Hotel</option>
                                        <option className="bg-[#121212]" value="Lainnya">{t.typeOther}</option>
                                    </select>
                                </Field>
                                <Field label={t.pack} htmlFor="paket">
                                    <select
                                        id="paket"
                                        value={form.paket}
                                        onChange={set("paket")}
                                        data-testid="form-select-paket"
                                        className={`${inputCls} appearance-none`}
                                    >
                                        <option className="bg-[#121212]" value="Basic">Basic</option>
                                        <option className="bg-[#121212]" value="Profesional">Profesional</option>
                                        <option className="bg-[#121212]" value="Luxury">Luxury</option>
                                        <option className="bg-[#121212]" value="Belum yakin, ingin konsultasi">
                                            {t.packUnsure}
                                        </option>
                                    </select>
                                </Field>
                            </div>
                            <Field label={t.msg} htmlFor="pesan">
                                <textarea
                                    id="pesan"
                                    rows={4}
                                    value={form.pesan}
                                    onChange={set("pesan")}
                                    placeholder={t.msgPh}
                                    data-testid="form-input-pesan"
                                    className={`${inputCls} resize-none`}
                                />
                            </Field>
                            <button
                                type="submit"
                                data-testid="form-submit-button"
                                className="btn-flame inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-semibold text-white shadow-xl shadow-orange-900/40 transition-transform duration-200 hover:scale-[1.02]"
                            >
                                <WhatsAppIcon className="h-5 w-5" />
                                {t.submit}
                            </button>
                            <p data-testid="form-note" className="text-center text-xs text-zinc-500">
                                {t.note}
                            </p>
                            <p
                                data-testid="form-hours-note"
                                className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-center text-xs leading-relaxed text-zinc-400"
                            >
                                {t.hours}
                            </p>
                        </form>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
};
